import Layout from 'components/layout';

import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';

import {
  getI18nData,
  isBrowser,
  useProductUrls
} from '@lucid/utils';
import dayjs from 'dayjs';
import { graphql } from 'gatsby';
import debounce from 'lodash/debounce';
import {
  Box,
  Heading,
  Icon,
  Link,
  Container,
  Collection,
  Text,
  TileGrid,
  TileFields
} from '@lucid/components-react';
import { Helmet } from 'react-helmet';
import {
  Drupal_FieldNodeWebinarFieldWebinarTypes,
  Drupal_Metatag,
  Drupal_NodeWebinar,
  Drupal_NodeWebinarHub,
  Drupal_TaxonomyTermWebinarTypes
} from 'generated/drupal-graphql';
import { mqMin, LucidTheme } from '@lucid/theme';
import { languages } from '@lucid/gatsby-plugin-i18n';

import { PageTemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';

const formatTiles = (tiles: Drupal_NodeWebinar[]) => {
  const formattedTiles = tiles.reduce<TileFields[]>((previous, current) => [
    ...previous,
    {
      title: current.fieldTitle as string || current.title as string,
      description: current.fieldWebinarDescription?.summary as string,
      date: current.fieldWebinarDate?.value as string,
      isSticky: current.fieldSticky!,
      url: current.entityUrl?.path as string,
      imageSrc: current.fieldImageSrc as string,
      nid: `${current.nid}`
    }
  ], []);

  return formattedTiles;
};

const WebinarHub: FC<PageTemplateProps> = ({ data, pageContext, location }) => {
  const {
    langPrefix,
    pageType,
    nid,
    i18nKey,
    page = 1,
    pageSize = 3,
    hubAlias,
    shouldHideSsr
  } = pageContext;

  const { urlLangPrefix } = languages[langPrefix];
  const { registerUrl } = useProductUrls(urlLangPrefix);
  const { drupal, i18nData } = data;
  const webinars = drupal.nodeQuery!.entities as Drupal_NodeWebinar[];
  const node = drupal.nodeById as Drupal_NodeWebinarHub;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const types = drupal.taxonomyTermQuery!
    .entities as Drupal_TaxonomyTermWebinarTypes[];
  const hubTypes = node.fieldWebinarTypes as Drupal_FieldNodeWebinarFieldWebinarTypes[];
  const hubTypeIds = hubTypes.map(type => type.targetId);
  const i18n = getI18nData(i18nData, i18nKey);
  const pastWebinars: Drupal_NodeWebinar[] = [];
  const now = dayjs();
  const upcomingWebinars: Drupal_NodeWebinar[] = webinars.filter(webinar => {
    const goLiveDate = webinar.fieldGoLiveDate?.value
      ? dayjs(webinar.fieldGoLiveDate.value).subtract(6, 'hour')
      : dayjs().subtract(1, 'year');
    if (goLiveDate < now) {
      const webinarDate = dayjs(webinar.fieldWebinarDate!.value!)
        .subtract(6, 'hour');
      if (webinarDate > now || webinar.fieldIsPerpetual) {
        return true;
      }
      // check if webinar should be included in page results
      // based on hub type and webinar type
      const matchingTypes = webinar.fieldWebinarTypes!.filter(type =>
        hubTypeIds.includes(type!.targetId)
      );

      if (matchingTypes.length > 0) {
        pastWebinars.push(webinar);
      }
    }

    return false;
  }).reverse();
  const pagedPastWebinars = pastWebinars.slice((page - 1) * pageSize);
  const [upcoming, setUpcoming] = useState<Drupal_NodeWebinar[] | []>([]);
  const [currentPage, setCurrentPage] = useState(page || 1);
  const [currentUpcomingPage, setCurrentUpcomingPage] = useState(page || 1);
  const [tiles, setTiles] = useState(pagedPastWebinars);
  const firstUpdate = useRef(true);
  const [search, setSearch] = useState('');

  const handleSearch = debounce((value: string) => {
    setSearch(value.toLowerCase());
  }, 300);

  const totalPageCount = Math.ceil(tiles.length / pageSize);
  const totalUpcomingPageCount = Math.ceil(upcoming.length / pageSize);

  const viewMore = (e: MouseEvent, isUpcomingWebinars: boolean) => {
    e.preventDefault();
    if (isUpcomingWebinars) {
      setCurrentUpcomingPage(currentPage + 1);
    }
    const newCurrentPage = currentPage + 1;
    setCurrentPage(newCurrentPage);
  };

  const renderHasMoreLink = (link: string, isUpcomingWebinars = false) => {
    return (
      <Box
        css={(theme: LucidTheme) => ({
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          position: 'relative',
          padding: `${theme.space[6]}px auto`,
          alignItems: 'center'
        })}
      >
        <hr
          css={(theme: LucidTheme) => ({
            border: 0,
            borderBottom: `2px solid ${theme.colors.coolGrey[10]}`,
            margin: 0,
            width: '100%'
          })}
        />
        <a
          css={(theme: LucidTheme) => ({
            cursor: 'pointer',
            padding: theme.space[2],
            whiteSpace: 'nowrap',
            background: 'transparent',
            border: 0,
            fontSize: theme.fontSizes[3],
            color: theme.colors.indigo[50],
            '&:visited': {
              color: theme.colors.indigo[70]
            },
            '&:hover': {
              color: theme.colors.indigo[60]
            }
          })}
          href={link}
          onClick={e => viewMore(e, isUpcomingWebinars)}
        >
          {i18n('mktg-ct-cs-view-more')}
          <Box
            css={(theme: LucidTheme) => ({
              display: 'inline-block',
              fontSize: theme.fontSizes[2],
              paddingLeft: theme.space[2]
            })}
          >
            <Icon type="ArrowRight" />
          </Box>
        </a>
      </Box>
    );
  };

  const hasMoreWebinars = currentPage < totalPageCount;
  const hasMoreUpcomingWebinars = currentUpcomingPage < totalUpcomingPageCount;

  useEffect(() => {
    if (isBrowser()) {
      setUpcoming(upcomingWebinars);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const inputEl = firstUpdate;
    if (inputEl.current) {
      firstUpdate.current = false;
      return;
    }

    const filteredWebinars = pastWebinars.filter(tile =>
      tile.fieldTitle!.toLowerCase().includes(search)
    );

    setCurrentPage(1);
    setTiles(filteredWebinars);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const titleMetatag = (entityMetatags as Drupal_Metatag[]).find(
    ({ key }) => key === 'title'
  );

  return (
    <Layout
      entityTranslations={entityTranslations}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      shouldHideSsr={shouldHideSsr}
    >
      <React.Fragment>
        {currentPage > 1 && (
          <Helmet>
            {titleMetatag ? (
              <title key={titleMetatag.key}>
                {`Page ${currentPage} for ${titleMetatag.value}`}
              </title>
            ) : null}
            <meta key="webinar-hub-robots-meta-script" content="follow, noindex" name="robots" />
          </Helmet>
        )}
        <Box as="main" css={{ position: 'relative' }}>
          <Box
            css={(theme: LucidTheme) => ({
              label: `${pageType}-hero`,
              backgroundColor: theme.colors.coolGrey[100],
              paddingTop: theme.space[8],
              paddingBottom: theme.space[8]
            })}
            id="upcoming"
          >
            <Container
              css={{
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Link to="/webinars" underline="none">
                <Heading
                  as="h1"
                  color="white"
                  mb={{
                    default: 6,
                    tabletSmall: 9
                  }}
                  variant="headline3XL"
                >
                  {i18n('mtkg-webinar-title')}
                </Heading>
              </Link>
              {page === 1 && (
                <Box className="webinar-hub__upcoming">
                  <Heading
                    as="h2"
                    color="white"
                    mb={{ default: 5, tabletLandscape: 0 }}
                    variant="headlineXL"
                  >
                    {i18n('mtkg-webinar-upcoming')}
                  </Heading>
                  <TileGrid
                    currentPage={currentUpcomingPage}
                    page={page}
                    pageType={pageType}
                    tiles={formatTiles(upcoming)}
                    tilesPerPage={pageSize}
                  />
                  {hasMoreUpcomingWebinars && (
                    renderHasMoreLink(`${hubAlias}/${currentUpcomingPage + 1}`, true)
                  )}
                  {!firstUpdate.current ? (
                    upcoming.length < 1 && (
                      <Box
                        css={(theme: LucidTheme) => ({
                          color: 'white',
                          textAlign: 'center',
                          padding: `${theme.space[4]}px ${theme.space[0]}px`
                        })}
                      >
                        {i18n('mtkg-webinar-upcoming-empty')}
                      </Box>
                    )
                  ) : (
                    <Box
                      css={(theme: LucidTheme) => ({
                        color: 'white',
                        textAlign: 'center',
                        padding: `${theme.space[4]}px ${theme.space[0]}px`
                      })}
                    >
                      Gathering upcoming webinars ...
                    </Box>
                  )}
                </Box>
              )}
            </Container>
          </Box>
          <Box
            as="section"
            css={(theme: LucidTheme) => ({
              backgroundColor: theme.colors.white,
              position: 'relative',
              paddingTop: theme.space[5],
              paddingBottom: theme.space[9],
              [mqMin.tabletSmall]: {
                paddingTop: theme.space[8]
              }
            })}
            id="on-demand"
          >
            <Container>
              <Box
                css={(theme: LucidTheme) => ({
                  label: 'filterSearchContainer',
                  flex: '0 1 100%',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                  paddingBottom: theme.space[7],
                  display: 'flex',
                  alignItems: 'flex-start',
                  maxWidth: '100%',
                  [mqMin.computer]: {
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }
                })}
              >
                <Box
                  css={{
                    label: 'links',
                    maxWidth: 'calc(50% - 20px)',
                    flex: '0 1 calc(50% - 20px)',
                    display: 'flex',
                    flexDirection: 'column',
                    listStyle: 'none',
                    [mqMin.tablet]: {
                      flexDirection: 'row',
                      flex: '0 1 75%',
                      maxWidth: '100%'
                    },
                    [mqMin.computer]: {
                      flex: '0 1 calc(50% - 15px)',
                      maxWidth: 'calc(50% - 15px)'
                    }
                  }}
                >
                  {types.map(type => (
                    <Link
                      key={type.fieldLinkUrl!}
                      css={(theme: LucidTheme) => ({
                        flex: '0 1 100%',
                        display: 'inline-block',
                        fontWeight: theme.fontWeights.strong,
                        order: location.pathname === type.fieldLinkUrl ? 0 : 1,
                        paddingBottom: theme.space[4],
                        [mqMin.tablet]: {
                          flex: 'unset',
                          paddingRight: theme.space[5],
                          paddingBottom: theme.space[5]
                        },
                        [mqMin.computer]: {
                          paddingBottom: 'unset'
                        }
                      })}
                      to={`${type.fieldLinkUrl!}#on-demand`}
                    >
                      {type.name}
                    </Link>
                  ))}
                </Box>
                <Box
                  css={(theme: LucidTheme) => ({
                    label: 'inputContainer',
                    flex: '0 1 100%',
                    display: 'inline-block',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: '20px',
                      height: '100%',
                      backgroundImage:
                            "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjM2Q0NzUyIiBkPSJNMjEuMTcyIDI0bC03LjM4Ny03LjM4N2MtMS4zODguODc0LTMuMDI0IDEuMzg3LTQuNzg1IDEuMzg3LTQuOTcxIDAtOS00LjAyOS05LTlzNC4wMjktOSA5LTkgOSA0LjAyOSA5IDljMCAxLjc2MS0uNTE0IDMuMzk4LTEuMzg3IDQuNzg1bDcuMzg3IDcuMzg3LTIuODI4IDIuODI4em0tMTIuMTcyLThjMy44NTkgMCA3LTMuMTQgNy03cy0zLjE0MS03LTctNy03IDMuMTQtNyA3IDMuMTQxIDcgNyA3eiIvPjwvc3ZnPg==')",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'left',
                      backgroundSize: '75%',
                      fill: theme.colors.coolGrey[40],
                      marginLeft: theme.space[3]
                    },
                    [mqMin.tablet]: {
                      flex: '0 1 calc(50% - 20px)',
                      maxWidth: 'calc(50% - 20px)'
                    },
                    [mqMin.computer]: {
                      flex: '0 1 calc(50% - 15px)',
                      maxWidth: 'calc(50% - 15px)'
                    }
                  })}
                >
                  <input
                    css={(theme: LucidTheme) => ({
                      label: 'input',
                      width: 'calc(100% - 25px)',
                      background: theme.colors.white,
                      color: theme.colors.coolGrey[70],
                      border: `1px solid ${theme.colors.coolGrey[40]}`,
                      height: '40px',
                      fontSize: theme.fontSizes[2],
                      borderRadius: '5px',
                      padding: `${theme.space[3]}px ${theme.space[3]}px ${theme.space[3]}px ${theme.space[6]}px`
                    })}
                    name="search"
                    onChange={e => handleSearch(e.target.value)}
                    placeholder="Type something"
                    type="text"
                  />
                </Box>
              </Box>
              <Box className="webinar-hub__webinar-list">
                <Heading as="h2" mb={5} mt={3} variant="headlineXL">
                  {i18n('mtkg-webinar-on-demand')}
                </Heading>
                {tiles.length > 0 ? (
                  <TileGrid
                    currentPage={currentPage}
                    page={page}
                    pageType={pageType}
                    tiles={formatTiles(tiles)}
                    tilesPerPage={pageSize}
                  />
                ) :
                    (
                      <Box>{i18n('mktg-webinar-search-no-results')}</Box>
                    )}
              </Box>
              {hasMoreWebinars && (
                renderHasMoreLink(`${hubAlias}/${currentPage + 1}`)
              )}
            </Container>
          </Box>
          <Container>
            <Box
              css={(theme: LucidTheme) => ({
                paddingBottom: theme.space[7],
                [mqMin.tabletSmall]: {
                  maxWidth: '680px'
                },
                [mqMin.computer]: {
                  paddingLeft: theme.space[10]
                }
              })}
            >
              <Heading as="h2" color="coolGrey.100" variant="headlineXL">
                {i18n('mktg-subfooter-header')}
              </Heading>
              <Text variant="paragraphM400">{i18n('mktg-subfooter-p')}</Text>
              <Collection mx={3} my={3}>
                <Link
                  displayAsButton
                  primary
                  to={`${registerUrl}/466e7eac-5163-05d7-b7f1-42440a00189f/`}
                >
                  {i18n('mktg-subfooter-cta-secondary')}
                </Link>
                <Link
                  displayAsButton
                  secondary
                  to="/contact/get-a-quote"
                >
                  {i18n('mktg-nav-contact-sales')}
                </Link>
              </Collection>
            </Box>
          </Container>
        </Box>
      </React.Fragment>
    </Layout>
  );
};

export default WebinarHub;

export const query = graphql`
  fragment WebinarHub on Drupal_NodeWebinarHub {
    fieldWebinarTypes {
      targetId
    }
  }

  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $i18nKey: String!
    $langPrefix: [String!]
  ) {
    drupal {
      nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "type", value: ["webinar"], operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
          ]
          groups: [
            {
              conjunction: OR
              conditions: [
                { field: "field_is_private", value: "false", operator: EQUAL }
                { field: "field_is_private", operator: IS_NULL }
              ]
            }
          ]
        }
        sort: [{ field: "field_webinar_date.value", direction: DESC }]
      ) {
        entities {
          ...Webinar
        }
      }
      nodeById(id: $nid, language: $LanguageId) {
        ...entityFields
        ...WebinarHub
      }
      taxonomyTermQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "vid", operator: IN, value: ["webinar_types"] }
            { field: "langcode", operator: EQUAL, value: $langPrefix }
          ]
        }
      ) {
        entities {
          ... on Drupal_TaxonomyTermWebinarTypes {
            name
            fieldLinkUrl
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mtkg_webinar_title
            mtkg_webinar_upcoming
            mtkg_webinar_upcoming_empty
            mtkg_webinar_on_demand
            mktg_ct_cs_view_more
            mktg_subfooter_header
            mktg_subfooter_p
            mktg_subfooter_cta_primary
            mktg_subfooter_cta_secondary
            mktg_webinar_search_no_results
            mktg_nav_contact_sales
          }
        }
      }
    }
  }
`;
